/**
 * Centralized VPS API client for MakeMyDoc.
 * Replaces Supabase Edge Function calls with direct API calls to the VPS.
 */

import { supabase } from './supabase';

const API_URL = import.meta.env.VITE_API_URL || '';

/** Get current JWT token from Supabase session */
async function getToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');
  return session.access_token;
}

/** Make an authenticated API call to the VPS */
async function vpsApiCall<T = unknown>(
  path: string,
  body: unknown,
  options?: { method?: string },
): Promise<T> {
  const token = await getToken();
  const method = options?.method || 'POST';

  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: method !== 'GET' ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    throw new Error(
      (errData as { error?: string }).error || `API error (${response.status})`,
    );
  }

  return response.json() as Promise<T>;
}

/** Make an authenticated GET call to the VPS */
async function vpsApiGet<T = unknown>(path: string): Promise<T> {
  const token = await getToken();

  const response = await fetch(`${API_URL}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    throw new Error(
      (errData as { error?: string }).error || `API error (${response.status})`,
    );
  }

  return response.json() as Promise<T>;
}

// =========================================
// Typed API functions
// =========================================

/** Sync import result */
export interface ImportSyncResult {
  imported: number;
  skipped_duplicates: number;
  failed: number;
  errors: string[];
  duplicates_detail?: Array<{
    title: string | null;
    reason: string;
    matching_title: string;
    similarity_score: number;
  }>;
}

/** Async import response (job enqueued) */
export interface ImportAsyncResult {
  job_id: string;
  status: 'queued';
  total: number;
  skipped_duplicates: number;
  duplicates_detail?: ImportSyncResult['duplicates_detail'];
}

/** Dry-run result */
export interface ImportDryRunResult {
  entries: Array<{
    index: number;
    title: string | null;
    status: 'new' | 'duplicate';
    reason?: string;
    matching_title?: string;
    similarity_score?: number;
  }>;
  new_count: number;
  duplicate_count: number;
}

/** Job status response */
export interface JobStatusResult {
  id: string;
  status: 'waiting' | 'active' | 'completed' | 'failed' | 'delayed' | 'paused';
  progress: { stage: string; current: number; total: number } | number;
  result?: ImportSyncResult;
  error?: string;
}

/** Import references (RIS/BIB/PubMed) */
export function importReferences(body: {
  entries: Array<{ metadata: Record<string, unknown>; raw: string }>;
  folder_id?: string;
  dry_run?: boolean;
  format?: string;
}) {
  return vpsApiCall<ImportSyncResult | ImportAsyncResult | ImportDryRunResult>('/api/import', body);
}

/** Extract text from a source file */
export function extractText(body: { source_id: string }) {
  return vpsApiCall('/api/extract', body);
}

/** Evaluate a source against criteria */
export function evaluateSource(body: {
  source_id: string;
  criteria_ids: string[];
  ai_model: string;
}) {
  return vpsApiCall('/api/evaluate', body);
}

/** Screen multiple sources */
export function screenSources(body: {
  source_ids: string[];
  criteria_ids: string[];
}) {
  return vpsApiCall<{ success: boolean; batch_size: number; results: Array<{ source_id: string; verdict: string; reason: string; score: number }> }>(
    '/api/screen',
    body,
  );
}

/** Generate a report */
export function generateReport(body: {
  folder_id: string;
  title: string;
  sections: Array<{ type: string; title: string; instructions?: string }>;
  ai_model?: string;
}) {
  return vpsApiCall('/api/report', body);
}

/** Detect duplicate sources */
export function detectDuplicates(body: { folder_id?: string }) {
  return vpsApiCall('/api/duplicates', body);
}

/** Predict screening priority */
export function predictPriority(body: { review_id: string; phase?: string }) {
  return vpsApiCall('/api/priority', body);
}

/** Extract structured data from a source */
export function extractData(body: {
  source_id: string;
  schema: Array<{ name: string; type: string; description: string; required: boolean }>;
  ai_model?: string;
}) {
  return vpsApiCall('/api/extract-data', body);
}

/** Get job status */
export function getJobStatus(jobId: string, queue: string) {
  return vpsApiGet<JobStatusResult>(`/api/jobs/${jobId}?queue=${queue}`);
}

export const api = {
  importReferences,
  extractText,
  evaluateSource,
  screenSources,
  generateReport,
  detectDuplicates,
  predictPriority,
  extractData,
  getJobStatus,
};
